import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import { SECTIONS_LEVEL_COUNTS } from '@/shared/constants/configurator';
import { getMaxUsedValues } from '@/shared/lib/configurator';
import { deepClone } from '@/shared/lib/json';
import { Shelf } from '@/shared/types/configurator';
import { Expanded } from '@/shared/ui/expanded/expanded';
import { Select } from '@/shared/ui/select/select';
import { useConfiguratorData } from '@/widgets/configurator';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_USED_OPTIONS } from '../../constants/toolbar.constants';
import { useConfiguratorOptions } from '../../hooks/use-configurator-options';
import {
	ToolbarConfiguratorOptions,
	ToolbarConfiguratorUsedOptions,
} from '../../types/toolbar.types';
import styles from './multi-section-editor.module.css';

const MultiSectionEditorContent = () => {
	const { t } = useTranslation();
	const { state, updateSections } = useConfigurator();
	const { data } = useConfiguratorData();
	const { heightOptions, widthOptions, depthOptions, levelsOptions, displayShelfDeckOptions } =
		useConfiguratorOptions();

	const currentOptions = useMemo(() => {
		const usedOptions: ToolbarConfiguratorUsedOptions = state.sections.reduce(
			(acc, section) => {
				acc.height.push(section.height);
				acc.width.push(section.width);
				acc.depth.push(section.depth);
				acc.levels.push(section.selfs.length);

				for (const shelf of section.selfs) {
					acc.deck[shelf.deck]++;
				}

				return acc;
			},
			deepClone(DEFAULT_USED_OPTIONS),
		);

		const mostUsedDeck = usedOptions[Math.max(...Object.keys(usedOptions.deck).map(Number))];
		const mostUsedOptions: ToolbarConfiguratorOptions = {
			height:
				+getMaxUsedValues(usedOptions.height) ||
				(data?.heights?.[state.material] || [0])[0],
			width: +getMaxUsedValues(usedOptions.width) || (data?.widths || [0])[0],
			depth: +getMaxUsedValues(usedOptions.depth) || (data?.depths || [0])[0],
			levels: +getMaxUsedValues(usedOptions.levels) || SECTIONS_LEVEL_COUNTS[0],
			deck: mostUsedDeck || displayShelfDeckOptions[0].value,
		};

		return mostUsedOptions;
	}, [state, displayShelfDeckOptions, data]);

	const handleUpdate = (data: Partial<ToolbarConfiguratorOptions>) => {
		const sectionIndexes = state.sections.map((_, index) => index);

		if (data.height) {
			updateSections(sectionIndexes, { height: data.height });
		}

		if (data.width) {
			updateSections(sectionIndexes, { width: data.width });
		}

		if (data.depth) {
			updateSections(sectionIndexes, { depth: data.depth });
		}

		if (data.levels) {
			const selfs = Array.from({ length: data.levels }).map<Shelf>(() => ({
				deck: currentOptions.deck,
			}));
			updateSections(sectionIndexes, { selfs });
		}

		if (data.deck) {
			useConfigurator.setState({
				state: {
					...state,
					sections: state.sections.map((section) => ({
						...section,
						selfs: section.selfs.map((shelf) => ({ ...shelf, deck: data.deck })),
					})),
				},
			});
		}
	};

	return (
		<div className={styles.container}>
			<Select
				options={heightOptions}
				value={currentOptions.height}
				label={t('size.height.sectionTitle')}
				onChange={(height) => handleUpdate({ height })}
			/>
			<Select
				options={widthOptions}
				value={currentOptions.width}
				label={t('size.width.sectionTitle')}
				onChange={(width) => handleUpdate({ width })}
			/>
			<Select
				options={depthOptions}
				value={currentOptions.depth}
				label={t('size.depth.sectionTitle')}
				onChange={(depth) => handleUpdate({ depth })}
			/>
			<Select
				options={levelsOptions}
				value={currentOptions.levels}
				label={t('size.levels.title')}
				onChange={(levels) => handleUpdate({ levels })}
			/>
			<Select
				value={currentOptions.deck}
				label={t('material.shelf.title')}
				options={displayShelfDeckOptions}
				onChange={(deck) => handleUpdate({ deck })}
			/>
		</div>
	);
};

export const MultiSectionEditor = () => {
	const { t } = useTranslation();

	return (
		<Expanded
			triggerContent={
				<div className={styles.trigger}>
					<p className={styles.triggerLabel}>{t('editor.multiEditor')}</p>
				</div>
			}
			content={<MultiSectionEditorContent />}
		/>
	);
};
